import { useCallback, type ReactNode } from 'react'
import { FeatherIcon } from '../../components/FeatherIcon'
import { Spinner } from '../../components/Spinner'
import { Sentinel } from '../../library/Sentinel'
import { useMediaSrc } from '../../library/useMediaSrc'
import { Avatar } from '../Avatar'
import { useStats, useStatsT } from './statsContext'
import { Spark } from './StatsChart'
import { avatarUrl, delta, descriptionParts, mediaCounts, thumbOf, userNames, type PillDef, type Raw, type Tone } from './statisticsData'
import type { ListState } from './statsStore'

/** A row of pill buttons (sub-tabs, metric pickers); labels are keys of `l`. */
export function Pills({
  items,
  active,
  onPick,
  small = false,
  disabled,
  label,
  className = '',
}: {
  items: readonly PillDef[]
  active: string
  onPick?: (id: string) => void
  small?: boolean
  disabled?: (id: string) => boolean
  label?: string
  className?: string
}) {
  const { t } = useStatsT()
  return (
    <div className={`ofx-pills ${small ? 'ofx-pills--small' : ''} ${className}`} role="group" aria-label={label}>
      {items.map(([id, key]) => (
        <button
          key={id}
          type="button"
          className={`ofx-pill ${id === active ? 'is-on' : ''}`}
          aria-pressed={id === active}
          disabled={disabled?.(id)}
          onClick={() => onPick?.(id)}
        >
          {t(`l.${key}`)}
        </button>
      ))}
    </div>
  )
}

export function Delta({ value, signed = false }: { value: unknown; signed?: boolean }) {
  const d = delta(value, signed)
  if (!d) return null
  return (
    <span className={`ofx-delta ${d.up ? 'is-up' : 'is-down'}`}>
      <span aria-hidden="true">{d.up ? '↗' : '↘'}</span>
      {d.text}
    </span>
  )
}

export function Card({ title, className = '', children }: { title?: string; className?: string; children: ReactNode }) {
  return (
    <section className={`ofx-card ${className}`} aria-label={title}>
      {title && <h3 className="ofx-card-title">{title}</h3>}
      {children}
    </section>
  )
}

export function CardEmpty({ text }: { text: string }) {
  return <div className="ofx-card-empty">{text}</div>
}

export function Loading() {
  const { t } = useStatsT()
  return (
    <div className="ofx-loading">
      <Spinner size={22} label={t('loading')} />
    </div>
  )
}

/** "Could not load: …" with Retry (retries every failed call of the page, like the CRM). */
export function ErrorLine({ message }: { message: string }) {
  const { t } = useStatsT()
  const { store } = useStats()
  return (
    <div className="ofx-error" role="alert">
      <span>{t('loadFailed', { error: message })}</span>
      <button type="button" className="ofx-link-btn" onClick={() => store.retry()}>
        {t('retry')}
      </button>
    </div>
  )
}

export function Empty({ text, icon = true }: { text: string; icon?: boolean }) {
  return (
    <div className="ofx-empty">
      {icon && <FeatherIcon name="bar-chart-2" size={40} className="ofx-empty-icon" />}
      <span>{text}</span>
    </div>
  )
}

/** A lazily paged list: spinner first, then rows, then more on scroll; empty / error states. */
export function ListBlock({ listKey, list, header, empty, children }: { listKey: string; list: ListState; header?: ReactNode; empty: string; children: ReactNode }) {
  const { store } = useStats()
  const more = useCallback(() => void store.loadMore(listKey), [store, listKey])
  if (!list.loaded && !list.rows.length && !list.error) return <Loading />
  if (!list.rows.length && !list.loading && !list.error) return <Empty text={empty} icon={false} />
  return (
    <>
      {header}
      {children}
      {list.error ? <ErrorLine message={list.error} /> : list.loading ? <Loading /> : list.hasMore ? <Sentinel onVisible={more} token={list.rows.length} /> : null}
    </>
  )
}

/** Avatar + name (+ "name @username"); the name opens the fan's chat when linked. */
export function UserCell({ user, linked = true }: { user: Raw | null | undefined; linked?: boolean }) {
  const { openChat } = useStats()
  const { primary, secondary, id } = userNames(user)
  return (
    <div className="ofx-user">
      <Avatar user={{ name: primary, avatar: avatarUrl(user) }} size={38} />
      <div className="ofx-user-text">
        {linked && id ? (
          <button type="button" className="ofx-user-name ofx-fan-link" onClick={() => openChat(id)}>
            {primary}
          </button>
        ) : (
          <span className="ofx-user-name">{primary}</span>
        )}
        {secondary && <small>{secondary}</small>}
      </div>
    </div>
  )
}

/** The first media's thumbnail, through the CRM media proxy (never the OnlyFans CDN directly). */
export function Thumb({ media, story = false }: { media: unknown; story?: boolean }) {
  const thumb = thumbOf(media)
  const { ref, src } = useMediaSrc(thumb?.url ?? '')
  if (!thumb) return null
  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className={`ofx-thumb ${story ? 'ofx-thumb--story' : ''}`}>
      {src && <img src={src} alt="" loading="lazy" />}
      {thumb.video && <FeatherIcon name="play" size={14} className="ofx-thumb-badge" />}
    </span>
  )
}

export function MediaCount({ media, count }: { media: unknown; count: unknown }) {
  const { t } = useStatsT()
  const c = mediaCounts(media)
  const parts: ReactNode[] = []
  if (c.photos) parts.push(<span key="p"><FeatherIcon name="image" size={13} />{c.photos}</span>)
  if (c.videos) parts.push(<span key="v"><FeatherIcon name="video" size={13} />{c.videos}</span>)
  if (c.audios) parts.push(<span key="a"><FeatherIcon name="mic" size={13} />{c.audios}</span>)
  if (!parts.length && Number(count) > 0) parts.push(<span key="n">{t('mediaCount', { n: count })}</span>)
  return parts.length ? <div className="ofx-media-count">{parts}</div> : null
}

/** Text clamped to two lines past 160 characters, with Read more / Show less. */
export function ReadMore({ id, text }: { id: string; text: string }) {
  const { t } = useStatsT()
  const { expanded, toggleExpanded } = useStats()
  const value = String(text || '').trim()
  if (!value) return null
  const long = value.length > 160
  const open = expanded.has(id)
  return (
    <>
      <div className={`ofx-text ${long && !open ? 'is-clamped' : ''}`}>{value}</div>
      {long && (
        <button type="button" className="ofx-link-btn" onClick={() => toggleExpanded(id)}>
          {t(open ? 'showLess' : 'readMore')}
        </button>
      )}
    </>
  )
}

export function SummaryItem({ label, value, change }: { label: string; value: string; change?: unknown }) {
  return (
    <div className="ofx-summary-item">
      <span>{label}</span>
      <strong>
        {value}
        <Delta value={change} signed />
      </strong>
    </div>
  )
}

export function SummaryGrid({ children, spark, tone = 's1' }: { children: ReactNode; spark?: unknown; tone?: Tone }) {
  return (
    <div className="ofx-summary-grid">
      {children}
      {spark !== undefined && spark !== null && (
        <div className="ofx-summary-spark">
          <Spark points={spark} tone={tone} />
        </div>
      )}
    </div>
  )
}

export function Kv({ label, value, className = '' }: { label: string; value: ReactNode; className?: string }) {
  return (
    <div className={`ofx-kv ${className}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

export function StatusIcon({ icon, label, tone }: { icon: string; label: string; tone: 'pending' | 'done' | 'reversed' | 'rejected' }) {
  return (
    <span className={`ofx-status ofx-status--${tone}`} role="img" tabIndex={0} title={label} aria-label={label}>
      <FeatherIcon name={icon} size={16} />
    </span>
  )
}

/** An OnlyFans transaction description; the fan's name opens their chat. */
export function Description({ row }: { row: Raw }) {
  const { openChat } = useStats()
  return (
    <span className="ofx-desc">
      {descriptionParts(row).map((p, i) =>
        p.fanId ? (
          <button key={i} type="button" className="ofx-fan-link" onClick={() => openChat(p.fanId!)}>
            {p.text}
          </button>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </span>
  )
}

export function TableHead({ cols, children }: { cols: string; children: ReactNode }) {
  return <div className={`ofx-table-head ofx-cols-${cols}`}>{children}</div>
}
